import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Badge } from "@/components/ui/badge";
import { Banknote, CreditCard, Lock } from "lucide-react";
import { cn } from "@/lib/utils";

interface PaymentMethodCardsProps {
  value: string;
  onChange: (value: string) => void;
  activeMethods: { method: string }[];
  cashSubtitle?: string;
}

const cardBase =
  "relative flex flex-col gap-3 p-5 rounded-xl border cursor-pointer transition-all duration-200 hover:border-primary/40";
const cardSelected =
  "has-[:checked]:border-primary has-[:checked]:border-2 has-[:checked]:bg-primary/5 has-[:checked]:shadow-sm";

const BrandPill = ({
  label,
  bg,
  fg = "#fff",
}: {
  label: string;
  bg: string;
  fg?: string;
}) => (
  <span
    className="inline-flex items-center justify-center px-2 py-1 rounded text-[10px] font-bold tracking-wide"
    style={{ background: bg, color: fg, minWidth: 42 }}
  >
    {label}
  </span>
);

export const PaymentMethodCards = ({
  value,
  onChange,
  activeMethods,
  cashSubtitle = "Pay your driver directly",
}: PaymentMethodCardsProps) => {
  const has = (m: string) => activeMethods.some((x) => x.method === m);

  return (
    <RadioGroup value={value} onValueChange={onChange} className="space-y-3">
      {has("cash") && (
        <label className={cn(cardBase, cardSelected, "flex-row items-center gap-4")}>
          <RadioGroupItem value="cash" />
          <div className="flex items-center justify-center w-10 h-10 rounded-lg bg-emerald-500/10 text-emerald-600 dark:text-emerald-400">
            <Banknote className="w-5 h-5" />
          </div>
          <div className="flex-1">
            <p className="font-sans font-semibold text-foreground">Cash</p>
            <p className="text-xs text-muted-foreground font-sans">{cashSubtitle}</p>
          </div>
        </label>
      )}

      {has("paypal") && (
        <label className={cn(cardBase, cardSelected)}>
          <div className="flex items-start gap-4">
            <RadioGroupItem value="paypal" className="mt-1" />
            <div className="flex items-center justify-center w-10 h-10 rounded-lg bg-[#003087]/10 shrink-0">
              <span className="text-[10px] font-extrabold leading-none">
                <span style={{ color: "#003087" }}>Pay</span>
                <span style={{ color: "#009cde" }}>Pal</span>
              </span>
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <p className="font-sans font-semibold text-foreground">PayPal Checkout</p>
                <Badge className="bg-primary/10 text-primary hover:bg-primary/15 border-0 text-[10px] font-bold uppercase tracking-wide">
                  Recommended
                </Badge>
              </div>
              <p className="text-xs text-muted-foreground font-sans mt-0.5">
                Pay with your PayPal account or credit/debit card
              </p>
            </div>
          </div>

          <div className="border-t border-border/60 pt-3 pl-10 space-y-2">
            <div className="flex items-center gap-1.5 flex-wrap">
              <BrandPill label="VISA" bg="#1A1F71" />
              <BrandPill label="MC" bg="#EB001B" />
              <BrandPill label="AMEX" bg="#2E77BB" />
              <BrandPill label="DISC" bg="#FF6000" />
              <BrandPill label="PayPal" bg="#003087" />
            </div>
            <p className="flex items-center gap-1.5 text-[11px] text-muted-foreground font-sans">
              <Lock className="w-3 h-3" />
              Secure payment · No PayPal account required
            </p>
          </div>
        </label>
      )}

      {has("stripe") && (
        <label className={cn(cardBase, cardSelected, "flex-row items-center gap-4")}>
          <RadioGroupItem value="stripe" />
          <div className="flex items-center justify-center w-10 h-10 rounded-lg bg-[#635BFF]/10 text-[#635BFF]">
            <CreditCard className="w-5 h-5" />
          </div>
          <div className="flex-1">
            <p className="font-sans font-semibold text-foreground">Credit / Debit Card</p>
            <p className="text-xs text-muted-foreground font-sans">Pay securely with Stripe</p>
          </div>
        </label>
      )}
    </RadioGroup>
  );
};

export default PaymentMethodCards;
